import type { HandledFormElementProps } from './types';
import type { FieldValues } from 'react-hook-form';

import { memo } from 'react';
import type { FC } from 'react';

/**
 * Creates a component that will be auto-registered with `react-hook-form` via `name` prop
 * @param WrappedComponent component to add additional props to
 * @returns component with additional props added (name, nextInputToFocus)
 */
const createHandledFormElement = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component extends FC<any>,
  SchemaType extends FieldValues
>(
  WrappedComponent: Component
) => {
  type Props = Parameters<Component>[0] & HandledFormElementProps<SchemaType>;

  const Component = memo((props: Props) => <WrappedComponent {...props} />);
  Component.displayName = `HandledFormElement(${WrappedComponent.displayName ?? WrappedComponent.name})`;
  return Component;
};

/**
 * Gets the name of the next input to focus
 * @param allChildrenWithNames names of all `children` passed to Form to be auto-registered with `react-hook-form` via `name` prop
 * @param currentChildName name of current child being mapped over
 * @returns Path<FieldValues> | undefined
 */
const getNextInputName = (
  allChildrenWithNames: string[],
  currentChildName: string
) => {
  const indexOfCurrentChild = allChildrenWithNames.indexOf(currentChildName);
  const nextIndex = indexOfCurrentChild > -1 ? indexOfCurrentChild + 1 : -1;

  if (nextIndex === -1) {
    return undefined;
  }

  return allChildrenWithNames[nextIndex];
};

export { createHandledFormElement, getNextInputName };
